import { StripeKycVerificationStates, useStripeKycBanner } from './hooks/useKycFloatingLockBanner';
import { CloseIcon, InProgressIcon, LockIcon, WarningIcon } from './icons';
import {
  CircularBackground,
  CloseButton,
  FloatingBannerContainer,
} from './KycFloatingLockBanner.styles';

import { Flex, Link, ThemeProvider, Typography } from '@aircall/tractor-v2';
import { KYC_VERIFICATION_ROUTE } from '@constants/routes.constants';
import { Trans, useTranslation } from 'react-i18next';

/**
 * KYC Floating Lock Banner component
 */
export function KycFloatingLockBanner() {
  const { t } = useTranslation();
  const { verificationState, handleClose, handleReopen } = useStripeKycBanner();

  const renderContent = () => {
    if (verificationState === StripeKycVerificationStates.POPUP_CLOSED) {
      return (
        <CircularBackground onClick={handleReopen}>
          <LockIcon />
        </CircularBackground>
      );
    }

    if (
      verificationState === StripeKycVerificationStates.NO_SHOW ||
      verificationState === StripeKycVerificationStates.USER_VERIFIED
    ) {
      return null;
    }

    const bannerConfig = {
      [StripeKycVerificationStates.NO_VERIFY_NO_ATTEMPT]: {
        icon: <LockIcon />,
        dataTest: 'stripe-kyc-banner-no-verify',
        titleKey: 'stripe.kyc.lock_banner.no_verify_no_attempt.title',
        descriptionKey: 'stripe.kyc.lock_banner.no_verify_no_attempt.description',
        actionKey: 'stripe.kyc.lock_banner.no_verify_no_attempt.action',
        linkComponent: (
          <Link href={KYC_VERIFICATION_ROUTE} variant='bodySemiboldM'>
            {t('stripe.kyc.lock_banner.no_verify_no_attempt.action')}
          </Link>
        ),
      },
      [StripeKycVerificationStates.VERIFICATION_IN_PROCESS]: {
        icon: <InProgressIcon />,
        dataTest: 'stripe-kyc-banner-in-process',
        titleKey: 'stripe.kyc.lock_banner.verification_in_progress.title',
        descriptionKey: 'stripe.kyc.lock_banner.verification_in_progress.description',
        linkComponent: null,
      },
      [StripeKycVerificationStates.VERIFICATION_REJECTED_FINAL]: {
        icon: <WarningIcon />,
        dataTest: 'stripe-kyc-banner-rejected',
        titleKey: 'stripe.kyc.lock_banner.verification_rejected.title',
        descriptionKey: 'stripe.kyc.lock_banner.verification_rejected.description',
        actionKey: 'stripe.kyc.lock_banner.verification_rejected.action',
        linkComponent: null,
      },
      [StripeKycVerificationStates.VERIFICATION_UNSUCCESSFUL]: {
        icon: <WarningIcon />,
        dataTest: 'stripe-kyc-banner-rejected',
        titleKey: 'stripe.kyc.lock_banner.verification_unsuccessful.title',
        descriptionKey: 'stripe.kyc.lock_banner.verification_unsuccessful.description',
        actionKey: 'stripe.kyc.lock_banner.verification_unsuccessful.action',
        linkComponent: (
          <Link href={KYC_VERIFICATION_ROUTE} variant='bodySemiboldM'>
            {t('stripe.kyc.lock_banner.verification_unsuccessful.action')}
          </Link>
        ),
      },
    };

    const config = bannerConfig[verificationState];

    return (
      <ThemeProvider>
        <FloatingBannerContainer>
          <Flex
            data-test={config.dataTest}
            flexDirection='column'
            alignItems='flex-start'
            alignSelf='stretch'
            padding='8px 12px'
            borderRadius='8px'
            background='linear-gradient(119deg, #195a75 2.16%, #052e3f 101.53%)'
            boxShadow='0 4px 30px 0 rgb(16 24 32 / 0.3)'
            gap='10px'
            position='relative'
          >
            <CloseButton onClick={handleClose}>
              <CloseIcon />
            </CloseButton>
            <Flex
              w='100%'
              flexDirection='column'
              alignItems='flex-start'
              padding='8px 12px'
              gap='xxs'
            >
              <Flex gap='xxxs' justifyContent='center' alignItems='center'>
                {config.icon}
                <Typography
                  variant='headingBoldXS'
                  textAlign='center'
                  color='warning-500'
                  margin='0'
                >
                  {t(config.titleKey)}
                </Typography>
              </Flex>
              <Flex margin='0'>
                <Typography variant='bodyRegularM' color='neutral-0'>
                  {config.linkComponent ? (
                    <Trans
                      components={[config.linkComponent]}
                      i18nKey={config.descriptionKey}
                      values={{
                        action: config.actionKey && t(config.actionKey),
                      }}
                    />
                  ) : (
                    t(config.descriptionKey)
                  )}
                </Typography>
              </Flex>
            </Flex>
          </Flex>
        </FloatingBannerContainer>
      </ThemeProvider>
    );
  };

  return renderContent();
}
